/**
 * Cloudflare R2 Storage management API service
 *
 * Provides methods for managing R2 configurations and storage operations.
 * All configurations are stored securely with encrypted credentials.
 */

import { apiClient } from './api'

// ===== Types =====

export type CloudflareR2Config = {
  id: string // UUID
  name: string
  account_id: string
  // Assets bucket configuration
  assets_bucket: string
  assets_public_domain: string | null
  // Agents bucket configuration
  agents_bucket: string
  agents_public_domain: string | null
  // Templates bucket configuration
  templates_bucket: string
  templates_public_domain: string | null
  // Support bucket configuration
  support_bucket: string
  support_public_domain: string | null
  is_active: boolean
  created_at: string
  updated_at: string
  created_by_id: string // UUID
}

export type CloudflareR2ConfigCreate = {
  name: string
  account_id: string
  access_key: string
  secret_key: string
  // Assets bucket configuration
  assets_bucket?: string
  assets_public_domain?: string
  // Agents bucket configuration
  agents_bucket?: string
  agents_public_domain?: string
  // Templates bucket configuration
  templates_bucket?: string
  templates_public_domain?: string
  // Support bucket configuration
  support_bucket?: string
  support_public_domain?: string
}

export type CloudflareR2ConfigUpdate = {
  name?: string
  account_id?: string
  access_key?: string
  secret_key?: string
  // Assets bucket configuration
  assets_bucket?: string
  assets_public_domain?: string
  // Agents bucket configuration
  agents_bucket?: string
  agents_public_domain?: string
  // Templates bucket configuration
  templates_bucket?: string
  templates_public_domain?: string
  // Support bucket configuration
  support_bucket?: string
  support_public_domain?: string
  is_active?: boolean
}

export type CloudflareR2ConfigListResponse = {
  configs: CloudflareR2Config[]
  total: number
}

export type R2Bucket = {
  name: string
  creation_date: string | null
}

export type R2BucketListResponse = {
  buckets: R2Bucket[]
  total: number
}

export type R2Object = {
  key: string
  size: number
  last_modified: string | null
  etag: string | null
  content_type: string | null
}

export type R2ObjectListResponse = {
  objects: R2Object[]
  total: number
  bucket: string
  prefix: string | null
  truncated: boolean
  continuation_token: string | null
}

export type R2ConnectionTestResponse = {
  success: boolean
  message: string
  account_id?: string
  buckets_count?: number
  bucket_accessible?: boolean
  bucket_tested?: string
}

export type R2TokenTest = {
  account_id: string
  access_key: string
  secret_key: string
  bucket?: string
}

// ===== API Functions =====

const cloudflareR2Api = {
  // ===== Configuration Management =====

  /**
   * Create a new Cloudflare R2 configuration
   */
  createConfig: (data: CloudflareR2ConfigCreate): Promise<CloudflareR2Config> => {
    return apiClient.post('/api/v1/cloudflare-r2/configs', data)
  },

  /**
   * List all Cloudflare R2 configurations
   */
  getConfigs: (): Promise<CloudflareR2ConfigListResponse> => {
    return apiClient.get('/api/v1/cloudflare-r2/configs')
  },

  /**
   * Get a specific Cloudflare R2 configuration
   */
  getConfig: (id: string): Promise<CloudflareR2Config> => {
    return apiClient.get(`/api/v1/cloudflare-r2/configs/${id}`)
  },

  /**
   * Update a Cloudflare R2 configuration
   */
  updateConfig: (id: string, data: CloudflareR2ConfigUpdate): Promise<CloudflareR2Config> => {
    return apiClient.put(`/api/v1/cloudflare-r2/configs/${id}`, data)
  },

  /**
   * Delete a Cloudflare R2 configuration
   */
  deleteConfig: (id: string): Promise<void> => {
    return apiClient.delete(`/api/v1/cloudflare-r2/configs/${id}`)
  },

  /**
   * Test connection to R2 using saved config
   */
  testConnection: (id: string): Promise<R2ConnectionTestResponse> => {
    return apiClient.post(`/api/v1/cloudflare-r2/configs/${id}/test`)
  },

  /**
   * Test R2 credentials without saving them
   */
  testCredentials: (data: R2TokenTest): Promise<R2ConnectionTestResponse> => {
    return apiClient.post('/api/v1/cloudflare-r2/test-credentials', data)
  },

  // ===== Bucket Operations =====

  /**
   * List all R2 buckets
   */
  getBuckets: (configId?: string): Promise<R2BucketListResponse> => {
    const params = configId ? `?config_id=${configId}` : ''
    return apiClient.get(`/api/v1/cloudflare-r2/buckets${params}`)
  },

  /**
   * List objects in a bucket
   */
  listObjects: (
    bucket: string,
    options?: {
      configId?: string
      prefix?: string
      maxKeys?: number
      continuationToken?: string
    },
  ): Promise<R2ObjectListResponse> => {
    const params = new URLSearchParams()
    if (options?.configId) params.append('config_id', options.configId)
    if (options?.prefix) params.append('prefix', options.prefix)
    if (options?.maxKeys) params.append('max_keys', options.maxKeys.toString())
    if (options?.continuationToken) params.append('continuation_token', options.continuationToken)

    const queryString = params.toString()
    return apiClient.get(
      `/api/v1/cloudflare-r2/buckets/${bucket}/objects${queryString ? `?${queryString}` : ''}`,
    )
  },
}

// ===== Helper Functions =====

/**
 * Format file size to human-readable string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Get icon class for file type based on extension
 */
export const getFileTypeIcon = (key: string): string => {
  const ext = key.split('.').pop()?.toLowerCase() || ''

  switch (ext) {
    // Images
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'ico':
      return 'image'
    // Documents
    case 'pdf':
      return 'file-text'
    case 'doc':
    case 'docx':
      return 'file-text'
    case 'xls':
    case 'xlsx':
      return 'table'
    // Archives
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
      return 'archive'
    // Code
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'html':
    case 'css':
    case 'json':
    case 'py':
    case 'go':
      return 'code'
    // Video
    case 'mp4':
    case 'webm':
    case 'avi':
    case 'mov':
      return 'video'
    // Audio
    case 'mp3':
    case 'wav':
    case 'ogg':
    case 'flac':
      return 'music'
    default:
      return 'file'
  }
}

/**
 * Check if file is an image based on extension
 */
export const isImageFile = (key: string): boolean => {
  const ext = key.split('.').pop()?.toLowerCase() || ''
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(ext)
}

/**
 * Get content type badge color
 */
export const getContentTypeBadgeColor = (contentType: string | null): string => {
  if (!contentType) return 'text-gray-600 bg-gray-100'

  if (contentType.startsWith('image/')) return 'text-purple-600 bg-purple-100'
  if (contentType.startsWith('video/')) return 'text-blue-600 bg-blue-100'
  if (contentType.startsWith('audio/')) return 'text-pink-600 bg-pink-100'
  if (contentType.startsWith('text/')) return 'text-green-600 bg-green-100'
  if (contentType.includes('json')) return 'text-yellow-600 bg-yellow-100'
  if (contentType.includes('pdf')) return 'text-red-600 bg-red-100'

  return 'text-gray-600 bg-gray-100'
}

export default cloudflareR2Api
